<template>
  <div id="modal-archive-purging-data">
    <p class="title">{{ mode === 'archive' ? 'Archive Data' : 'Purging Data' }}</p>

    <div class="information-wrap">
      <img src="@/assets/icon/info.svg" >
      <p class="information-title">Information</p>
      <div />
      <p
        v-if="mode === 'archive'"
        class="information-desc">
        Archiving data runs in the background which takes a long time.
        You can see this process in the activity log of this data manager.
      </p>
      <p
        v-if="mode === 'purging'"
        class="information-desc">
        Purging data runs in the background which takes a long time.
        You can see this process in the activity log of this data manager.
      </p>
    </div>

    <b-form class="mb-3">
      <label> Table </label>
      <j-input
        v-model="selectedTableName"
        :error-required="false"
        disabled />
    </b-form>

    <b-form
      v-if="mode === 'archive'"
      class="mb-3"
    >
      <label> Last Data on Date </label>
      <j-date-time
        :data="lastData"
        :max-datetime="maxLastDate"
        :error-required="false"
        @getTimestamp="setValueLasData"
      />
    </b-form>

    <div
      v-if="mode === 'purging' && !isLoading && lastArchiveInfo"
      class="mb-3 archive-info-wrap"
    >
      <div class="archive-info-top">
        <img
          :src="lastArchiveInfo.user_info ? lastArchiveInfo.user_info.photo_url : defaultImage"
          class="info-image"
        >
        <span class="info-name">
          {{ lastArchiveInfo.user_info ?
          lastArchiveInfo.user_info.full_name : '' }}
        </span>
        <span class="info-time">
          {{ formatDateTime(lastArchiveInfo.timestamp) }}
        </span>
      </div>
      <div class="archive-info-bottom">
        <span class="archive-label">
          {{ lastArchiveInfo.action.split('_').join(' ') }}
        </span>
        <span
          v-if="lastArchiveInfo.additional_info &&
          lastArchiveInfo.additional_info.last_data"
          class="date-label">
          {{ formatDate(lastArchiveInfo.additional_info.last_data) }}
        </span>
      </div>
    </div>

    <div
      v-if="mode === 'purging' && !isLoading && !lastArchiveInfo"
      class="mb-3 archive-not-found">
      <p>
        Archive Data No Available. Please Archive Data First Before Purging.
      </p>
    </div>

    <div
      v-if="mode === 'purging' && isLoading"
      class="mb-3 archive-info-skeleton"/>

    <b-form>
      <label>Remarks</label>
      <j-textarea
        v-model="remarks"
      />
    </b-form>

    <b-form class="otp-wrap">
      <label> OTP (One Time Password) </label>
      <div
        v-if="!isShowRequestOtp"
        class="message-wrap"
      >
        Please check your inbox, including your spam folder, to find the email containing your OTP.
      </div>
      <div class="input-otp-wrap">
        <input
          v-for="(otp, index) in Object.keys(otpObject)"
          v-model="otpObject[otp]"
          :key="otp"
          :tab-index="index"
          :disabled="isDisabledInputOtp"
          class="otp"
          type="text"
          maxlength="1"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          @keyup="handleInput"
          @paste="handlePaste"
        >
      </div>
      <div class="otp-info">
        <span
          v-if="isShowRequestOtp"
          class="text-blue"
          @click="requestOtp">
          Request OTP
        </span>
        <div v-else>
          <p
            v-if="!expired"
            class="text-black">
            Code is valid for {{ timerExpiredOtp }} minutes
          </p>
          <p>
            <span
              v-if="!expired"
              class="text-gray"
            >
              Didn't receive code?
            </span>
            <span
              v-if="expired"
              class="text-gray"
            >
              The code has expired,
            </span>
            <span
              :class="{'disabled-resend' : isDisabledResendOtp}"
              class="text-blue"
              @click="requestOtp"
            >
              Resend a new code
              <span v-if="isDisabledResendOtp">
                ({{ timerCountReqNewOtp }}s)
              </span>
            </span>
          </p>
        </div>
      </div>
    </b-form>

    <div class="archive-purging-buttons">
      <b-button
        class="white"
        @click="closeModal">
        Close
      </b-button>
      <b-button
        :disabled="!isShowConfirmBtn"
        class="blue"
        @click="handleConfirm"
      >
        Confirm
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import jDateTime from '@/components/inputs/jDateTime';
import jMixArchivePurging from '@/mixins/form-data/jMixArchivePurging';

export default {
  components: {
    jDateTime,
  },

  mixins: [jMixArchivePurging],

  props: {
    mode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      otpObject: { a: '', b: '', c: '', d: '', e: '', f: '' },
      otpToken: null,
      lastData: null,
      lastArchiveInfo: null,
      waitTime: null,
      expired: false,
      timerCountReqNewOtp: 0,
      timerExpiredOtp: '',
      durationExpired: 0,
      timerExpFunction: null,
      isShowRequestOtp: true,
      isDisabledInputOtp: true,
      isDisabledResendOtp: false,
      isLoading: true,
      paramsPageCount: 1,
      remarks: '',
    };
  },

  computed: {
    ...mapState('jStoreForm', ['form_data', 'audit_log_list']),

    selectedTableName() {
      return this.form_data.name;
    },

    selectedFormDataId() {
      return this.form_data.id;
    },

    maxLastDate() {
      return String(this.$moment(new Date()).subtract(7, 'days').format('YYYY-MM-DD'));
    },

    isShowConfirmBtn() {
      const isOtpObjectNotEmpty = Object.values(this.otpObject).every(i => i);
      if (this.mode === 'archive') {
        return this.lastData && this.remarks && isOtpObjectNotEmpty;
      }
      return this.lastArchiveInfo && this.remarks && isOtpObjectNotEmpty;
    },

    defaultImage() {
      // eslint-disable-next-line global-require
      const img = require('@/assets/logo/user1.png');
      return img;
    },
  },

  watch: {
    async mode(val) {
      if (val && val === 'purging') {
        const params = {
          form_data_id: this.selectedFormDataId,
          page: this.paramsPageCount,
          limit: 10,
          sort: { timestamp: -1 },
        };
        this.getListAuditLogTable(params);
      }

      this.remarks = '';
    },

    otpToken(val) {
      if (val) this.isDisabledInputOtp = false;
    },

    expired(val) {
      if (val) this.isDisabledInputOtp = true;
    },

    audit_log_list(val) {
      if (this.mode) {
        if (val && val.length > 0) {
          const archiveData = val.find(i => i.action === 'ARCHIVE_DATA');
          if (archiveData) {
            this.isLoading = false;
            this.lastArchiveInfo = archiveData;
          } else {
            this.paramsPageCount = this.paramsPageCount + 1;
            const params = {
              form_data_id: this.selectedFormDataId,
              page: this.paramsPageCount,
              limit: 10,
              sort: { timestamp: -1 },
            };
            this.getListAuditLogTable(params);
          }
        } else {
          this.isLoading = false;
          this.lastArchiveInfo = null;
        }
      }
    },
  },

  methods: {
    ...mapActions('jStoreForm', ['requestOtpArchiveData', 'requestOtpPurgingData', 'archiveData', 'purgingData', 'getListAuditLogTable']),

    selectIfNotEmpty(e) {
      if (e.target.value) {
        e.target.select();
      }
    },

    handleInput(e) {
      // Back to previous otp input
      if (e.keyCode === 37 && e.target.previousElementSibling) {
        e.preventDefault();
        e.target.previousElementSibling.focus();
      }

      // Next to previous otp input
      if (e.keyCode === 39 && e.target.nextElementSibling) {
        e.preventDefault();
        e.target.nextElementSibling.focus();
      }

      // Delete otp input
      if (e.keyCode === 8 && e.target.value === '' && e.target.previousElementSibling) {
        e.target.previousElementSibling.focus();
      }

      // Handle if number inputed, focus to next input
      if (e.keyCode >= 48 && e.keyCode <= 57 && e.target.nextElementSibling) {
        e.target.nextElementSibling.focus();
      }
    },

    handlePaste(e) {
      const data = e.clipboardData.getData('text');
      const otpKeys = Object.keys(this.otpObject);

      otpKeys.forEach((item, i) => {
        this.otpObject[item] = data[i];
      });

      e.target.blur();
    },

    async requestOtp() {
      if (!this.isDisabledResendOtp) {
        this.expired = false;
        let response;
        if (this.mode === 'archive') {
          response = await this.requestOtpArchiveData(this.selectedFormDataId);
        } else {
          response = await this.requestOtpPurgingData(this.selectedFormDataId);
        }
        if (response) {
          this.otpToken = response.otp_token;
          this.waitTime = response.expired_timestamp;
          this.isShowRequestOtp = false;
          this.handleTimer();
        }
      }
    },

    handleTimer() {
      const now = new Date().getTime();
      const distance = this.waitTime - now;
      const duration = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      clearInterval(this.timerExpFunction);
      // timer otp expired
      this.timerExpFunction = null;
      this.durationExpired = 60 * duration;
      this.timerExpiredOtp = this.$moment(this.durationExpired * 1000).format('mm:ss');

      // timer req new otp
      this.timerCountReqNewOtp = 30;
      this.isDisabledResendOtp = true;

      this.timerExpFunction = setInterval(() => {
        this.durationExpired -= 1;
        this.timerExpiredOtp = this.$moment(this.durationExpired * 1000).format('mm:ss');

        if (this.timerCountReqNewOtp >= 0) {
          this.timerCountReqNewOtp -= 1;
        }

        if (this.timerCountReqNewOtp === 0) {
          this.isDisabledResendOtp = false;
        }

        if (this.durationExpired === 0) {
          clearInterval(this.timerExpFunction);
          this.expired = true;
        }
      }, 1000);
    },

    handleArchiveData() {
      const params = {
        id: this.selectedFormDataId,
        data: {
          last_data: this.convertDate(this.lastData),
          otp: Object.values(this.otpObject).join(''),
          otp_token: this.otpToken,
          remarks: this.remarks.trim(),
        },
      };

      this.archiveData(params);
    },

    handlePurgingData() {
      const params = {
        id: this.selectedFormDataId,
        data: {
          archive_id: this.lastArchiveInfo.additional_info.archive_id,
          otp: Object.values(this.otpObject).join(''),
          otp_token: this.otpToken,
          remarks: this.remarks.trim(),
        },
      };

      this.purgingData(params);
    },

    handleConfirm() {
      if (this.mode === 'archive') this.handleArchiveData();
      if (this.mode === 'purging') this.handlePurgingData();

      this.closeModal();
    },

    closeModal() {
      this.resetData();
      this.$emit('close');
    },

    formatDateTime(timestamp) {
      return this.$moment(timestamp).format('DD MMMM YYYY HH:mm:ss');
    },

    formatDate(timestamp) {
      return this.$moment(timestamp).format('DD MMMM YYYY');
    },

    convertDate(data) {
      if (data) {
        const date = new Date(data);
        const years = date.getFullYear();
        const mounth = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
        const dates = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        const fullStringDate = `${years}-${mounth}-${dates}T23:59:59`;

        return Date.parse(fullStringDate);
      }
      return null;
    },

    setValueLasData(val) {
      this.lastData = val;
    },

    resetData() {
      this.otpObject = { a: '', b: '', c: '', d: '', e: '', f: '' };
      this.otpToken = null;
      this.lastData = null;
      this.lastArchiveInfo = null;
      this.waitTime = null;
      this.expired = false;
      this.isShowRequestOtp = true;
      this.isDisabledInputOtp = true;
      this.isDisabledResendOtp = false;
      this.isLoading = true;
      this.paramsPageCount = 1;
      this.remarks = '';
      clearInterval(this.timerExpFunction);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/data-manager/popup/ModalArchivePurgingData.vue